import { isRecord } from "@/utils/isRecord";
import type { NoxusUIMessage } from "../noxus-message-types";
import type { ToolPartBase } from "../ToolComponents";

// A plain Reject's reason; must match the backend approval capability's.
export const DEFAULT_DENY_REASON = "Denied by user";
// What Always allow sends as the reason; the backend's _ALWAYS_ALLOW_SENTINEL.
export const ALWAYS_ALLOW_REASON = "__always_allow__";

export type PendingApproval = {
  approvalId: string;
  // The raw tool part, so the composer resolves its icon/label through
  // ``resolveToolDisplay`` — the same source the message card uses.
  part: ToolPartBase;
  // False when the backend refuses "Always allow" for this tool (it deletes
  // data, so it asks every time).
  alwaysAllow: boolean;
  // Which of the calls this turn asked about it is, counting from 1.
  position: number;
  total: number;
};

// The approval payload the SDK attaches to a tool part; not in the broad
// part union, so we narrow structurally where we read it.
type ApprovalToolPart = {
  type: string;
  toolCallId: string;
  input?: unknown;
  approval?: { id?: string; approved?: boolean };
  callProviderMetadata?: { noxus?: Record<string, unknown> };
};

type ApprovalReadable = {
  type?: string;
  state?: string;
  approval?: { approved?: boolean; reason?: string } | null;
  callProviderMetadata?: { noxus?: Record<string, unknown> };
};

type ApprovalDecision = {
  approved: boolean;
  reason?: string;
  alwaysAllow?: boolean;
};

function readReloadDecision(
  part: ApprovalReadable
): ApprovalDecision | undefined {
  const approval = part.callProviderMetadata?.noxus?.["approval"];
  if (
    approval != null &&
    typeof approval === "object" &&
    "approved" in approval &&
    typeof (approval as { approved?: unknown }).approved === "boolean"
  ) {
    const { approved, reason, always_allow } = approval as {
      approved: boolean;
      reason?: unknown;
      always_allow?: unknown;
    };
    return {
      approved,
      reason: typeof reason === "string" ? reason : undefined,
      alwaysAllow: always_allow === true,
    };
  }
  return undefined;
}

// The user's approve/deny decision on an approval-gated tool part, or undefined
// if it never went through approval. It rides the SDK's native ``approval``
// field during the live turn, and the reload metadata channel on reload.
export function getApprovalDecision(
  part: ApprovalReadable
): ApprovalDecision | undefined {
  const live = part.approval;
  const stored = readReloadDecision(part);
  if (live != null && typeof live.approved === "boolean") {
    return {
      approved: live.approved,
      reason: live.reason,
      alwaysAllow:
        live.reason === ALWAYS_ALLOW_REASON || stored?.alwaysAllow === true,
    };
  }
  return stored;
}

// How a decided call reads, on its row and on its card.
export function decisionWord(decision: {
  approved: boolean;
  alwaysAllow?: boolean;
}): string {
  if (!decision.approved) return "Rejected";
  return decision.alwaysAllow ? "Always allowed" : "Allowed";
}

// What the user wrote when they rejected a call, so a reopened chat shows
// what Genie's next reply answers: during the turn, the reason the composer
// sent (unless it was a plain Reject); after a reload, the note the backend
// stored with the decision. A reloaded part also carries the native approval,
// whose reason is the text the model was given, so the stored note wins.
export function deniedWith(part: ApprovalReadable): string | undefined {
  const stored = part.callProviderMetadata?.noxus?.["approval"];
  if (isRecord(stored)) {
    const note = stored.user_note;
    return typeof note === "string" && note.trim() ? note.trim() : undefined;
  }
  const live = part.approval;
  if (live == null || live.approved !== false) return undefined;
  const text = (live.reason ?? "").trim();
  return text && text !== DEFAULT_DENY_REASON ? text : undefined;
}

function hasStampedApprovalDecision(part: ApprovalReadable): boolean {
  return readReloadDecision(part) !== undefined;
}

export function isActionablePendingApproval(part: ApprovalReadable): boolean {
  return (
    (part.state === "approval-requested" ||
      part.state === "approval-responded") &&
    !hasStampedApprovalDecision(part)
  );
}

function isAwaitingApproval(part: NoxusUIMessage["parts"][number]): boolean {
  if (part.type?.startsWith("tool-") !== true) return false;
  if (!("state" in part) || part.state !== "approval-requested") return false;
  // A part that already carries a decision (answered mid-stream, or re-stamped
  // from memory on reload) is settled — don't resurface the card. On refresh
  // the resumed part can briefly arrive as approval-requested while already
  // decided, which would otherwise flash the approval composer.
  return getApprovalDecision(part) === undefined;
}

// "tool-workflows_build_ab12cd" → "workflows_build" (drops the SDK's
// "tool-" prefix and any per-agent hash suffix).
export function toolNameFromPartType(partType: string): string {
  return partType.replace(/^tool-/, "").replace(/_[a-f0-9]{6,}$/, "");
}

// The first tool call in the LAST message still waiting on the user's
// approve/deny decision, or null when there's nothing to approve. Only the
// last message counts: the SDK's addToolApprovalResponse can only rewrite
// parts of the last message, so an approval-requested part anywhere earlier
// (stale deferred call rendered on reload) is unanswerable — surfacing it
// would block the composer with buttons that no-op.
export function getPendingApproval(
  messages: NoxusUIMessage[]
): PendingApproval | null {
  const last = messages[messages.length - 1];
  if (!last) return null;
  // The calls this round asked about: answered ones stay approval-responded
  // until the last is answered and the turn resumes.
  const round = last.parts.filter(
    (part) =>
      "state" in part &&
      (part.state === "approval-requested" ||
        part.state === "approval-responded")
  );
  const index = round.findIndex(isAwaitingApproval);
  if (index < 0) return null;
  const part = round[index];
  const p = part as unknown as ApprovalToolPart;
  return {
    approvalId: p.approval?.id ?? p.toolCallId,
    part: part as unknown as ToolPartBase,
    alwaysAllow: p.callProviderMetadata?.noxus?.["always_allow"] !== false,
    position: index + 1,
    total: round.length,
  };
}

// True while any tool call is still waiting on the user's approve/deny
// decision. The composer reads this to swap its input for the approval
// prompt until every pending card is answered.
export function hasUnansweredApproval(messages: NoxusUIMessage[]): boolean {
  return getPendingApproval(messages) !== null;
}
